/**
 * About Page — app version, links to website/telegram/support.
 */

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import logoWhite from '../../../assets/turnifly-logo-white.png'

interface AboutPageProps {
  isOpen: boolean
  onClose: () => void
}

const APP_VERSION = '1.0.0'

export const AboutPage: React.FC<AboutPageProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ y: '100%', opacity: 0.8 }} animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.8 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-50 bg-bg flex flex-col inset-x-0 bottom-0 rounded-t-[32px] max-h-[80vh] lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-3xl lg:max-h-[70vh] lg:w-full lg:max-w-sm lg:border lg:border-white/[0.06] lg:shadow-glass"
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-2 shrink-0">
              <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04] tap-highlight-transparent">
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">{t('about.title')}</h2>
              <div className="w-10" />
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-8">
              <div className="flex flex-col items-center py-6 text-center">
                <img src={logoWhite} alt="Turnifly" className="h-10 w-auto mb-4" />
                <h3 className="text-lg font-display font-bold text-text">Turnifly</h3>
                <p className="text-sm text-text-hint mt-1">{t('about.tagline')}</p>
                <p className="text-xs text-text-hint/50 mt-3">v{APP_VERSION}</p>

                <div className="w-full mt-6 space-y-2">
                  <LinkRow label={t('about.website')} href="https://turnifly.com" />
                  <LinkRow label={t('about.telegram')} href="https://t.me/Turnifly" />
                  <LinkRow label={t('about.support')} href="https://t.me/TurniflyBot" />
                </div>

                <p className="text-xs text-text-hint/30 mt-8">
                  {t('about.copyright')}
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

const LinkRow: React.FC<{ label: string; href: string }> = ({ label, href }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center justify-between px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] transition-colors tap-highlight-transparent"
  >
    <span className="text-sm text-text">{label}</span>
    <ExternalLink size={14} className="text-text-hint" />
  </a>
)
